import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { User } from 'oidc-client-ts';
import {
  ErrorType,
  NotFoundObjectError,
  ServerError,
  ValidationErrorModel,
} from '../../apps/benefit/models/Errors';
import { HttpStatuses } from '../enums';
import { config } from '../global/config';
import { logTrace } from '../telemetry/logTrace';

export type HttpResponse<T> = {
  data: T;
  hasErrors: boolean;
  error?: ValidationErrorModel | NotFoundObjectError | ServerError;
  errorCode?: string;
  errorType?: ErrorType;
};

export const getUser = () => {
  const oidcStorage = localStorage.getItem(
    `oidc.user:${config.idpAuthority}:${config.idpClientId}`,
  );

  if (!oidcStorage) {
    return null;
  }

  return User.fromStorageString(oidcStorage);
};

export class Http {
  public token?: string;
  private static languageGetter: () => string = () => config.defaultLanguage;

  public static getLanguage(): string {
    return Http.languageGetter();
  }

  // This will be called from the CustomerInfoProvider to update the language
  public static setLanguageGetter(getter: () => string): void {
    Http.languageGetter = getter;
  }
  constructor() {
    this.token = getUser()?.access_token;
    axios.interceptors.request.use(axiosConfig => {
      const headers = getAuthHeaders();

      axiosConfig.headers['Authorization'] = headers.Authorization;
      axiosConfig.headers['Language'] = headers.Language;
      axiosConfig.withCredentials = true;
      return axiosConfig;
    });

    axios.interceptors.response.use(response => {
      if (
        response.status === 204 &&
        response.headers['x-uc-action'] === 'redirect-login'
      ) {
        /*user context validation failed, invalid access token or invalid use of it*/
        globalThis.location.href = '/?invalid_session=true';
        return new Promise(() => {});
      }
      return response;
    });
  }

  private handleErrors<T>(error: AxiosError): HttpResponse<T> {
    if (error.code == 'ERR_CANCELED') {
      return {
        hasErrors: true,
        data: {} as T,
        errorCode: error.code,
      };
    }

    const status = error.response?.status;
    const data = error.response?.data;

    if (status === 419 || status === 420) {
      logTrace('ajax: dispatching auth:customer-status-error', {
        status,
        url: error.config?.url,
      });
      globalThis.dispatchEvent(
        new CustomEvent('auth:customer-status-error', {
          detail: { statusCode: status },
        }),
      );

      return {
        hasErrors: true,
        data: {} as T,
        errorCode: `custom_${status}`,
        errorType: ErrorType.UnAuthorizedError,
      };
    }

    switch (status) {
      case HttpStatuses.Unauthorized:
        return {
          hasErrors: true,
          data: {} as T,
          error: undefined,
          errorCode: 'errors.unauthorizedError',
          errorType: ErrorType.UnAuthorizedError,
        };
      case HttpStatuses.NotFound:
        return {
          hasErrors: true,
          data: {} as T,
          error: data as NotFoundObjectError,
          errorCode: 'errors.notfound',
          errorType: ErrorType.NotFoundError,
        };
      case HttpStatuses.PreconditionFailed:
        return {
          hasErrors: true,
          data: {} as T,
          error: data as ValidationErrorModel,
          errorType: ErrorType.ValidationError,
        };
      case HttpStatuses.InternalServerError:
      default:
        return {
          hasErrors: true,
          data: {} as T,
          error: data as ServerError,
          errorCode: 'errors.internalServerError',
          errorType: ErrorType.InternalServerError,
        };
    }
  }

  private getConfigWithCustomHeaders(
    config?: AxiosRequestConfig,
  ): AxiosRequestConfig {
    return {
      ...config,
      headers: {
        ...config?.headers,
        Location: window.location.href,
      },
    };
  }

  async get<T = any>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<HttpResponse<T>> {
    return axios
      .get(url, this.getConfigWithCustomHeaders(config))
      .then(response => {
        return { hasErrors: false, data: response.data };
      })
      .catch((error: AxiosError) => {
        return this.handleErrors(error);
      });
  }

  async delete<T = any>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<HttpResponse<T>> {
    return axios
      .delete(url, this.getConfigWithCustomHeaders(config))
      .then(response => {
        return { hasErrors: false, data: response.data };
      })
      .catch((error: AxiosError) => {
        return this.handleErrors(error);
      });
  }

  async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<HttpResponse<T>> {
    return axios
      .post(url, data, this.getConfigWithCustomHeaders(config))
      .then(response => {
        return { hasErrors: false, data: response.data };
      })
      .catch((error: AxiosError) => {
        return this.handleErrors(error);
      });
  }

  async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<HttpResponse<T>> {
    return axios
      .patch(url, data, this.getConfigWithCustomHeaders(config))
      .then(response => {
        return { hasErrors: false, data: response.data };
      })
      .catch((error: AxiosError) => {
        return this.handleErrors(error);
      });
  }

  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<HttpResponse<T>> {
    return axios
      .put(url, data, this.getConfigWithCustomHeaders(config))
      .then(response => {
        return { hasErrors: false, data: response.data };
      })
      .catch((error: AxiosError) => {
        return this.handleErrors(error);
      });
  }

  async downloadFile(
    url: string,
    filename: string,
    extension: string,
    config?: AxiosRequestConfig,
  ): Promise<HttpResponse<null>> {
    return axios
      .get(url, {
        ...this.getConfigWithCustomHeaders(config),
        responseType: 'blob',
      })
      .then(response => {
        const rawContentDisposition = response.headers['content-disposition'];
        const rawContentType = response.headers['content-type'];

        const contentDisposition =
          typeof rawContentDisposition === 'string'
            ? rawContentDisposition
            : undefined;
        const contentType =
          typeof rawContentType === 'string' ? rawContentType : undefined;

        const fileExtension =
          getExtensionFromContentDisposition(contentDisposition) ??
          getExtensionFromContentType(contentType) ??
          extension;

        const href = URL.createObjectURL(response.data);

        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', `${filename}.${fileExtension}`);
        link.click();

        URL.revokeObjectURL(href);

        return { hasErrors: false, data: null };
      })
      .catch((error: AxiosError) => {
        return this.handleErrors(error);
      });
  }

  async openPdf(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<HttpResponse<null>> {
    const pdfWindow = window.open('', '_blank');

    if (!pdfWindow) {
      return {
        hasErrors: true,
        data: null,
        errorCode: 'popup_blocked',
      };
    }

    return axios
      .get(url, {
        ...this.getConfigWithCustomHeaders(config),
        responseType: 'blob',
      })
      .then(response => {
        const rawContentType = response.headers['content-type'];
        const contentType =
          typeof rawContentType === 'string'
            ? rawContentType
            : 'application/pdf';
        const pdfBlob = new Blob([response.data], {
          type: contentType,
        });
        const blobUrl = URL.createObjectURL(pdfBlob);

        pdfWindow.location.replace(blobUrl);

        setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
        }, 60000);

        return {
          hasErrors: false,
          data: null,
        };
      })
      .catch((error: AxiosError) => {
        pdfWindow.close();

        return this.handleErrors(error);
      });
  }

  async printFile(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<HttpResponse<null>> {
    return axios
      .get(url, { ...config, responseType: 'blob' })
      .then(response => {
        const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(pdfBlob);

        const printWindow = window.open(blobUrl, '_blank');

        if (!printWindow) {
          return { hasErrors: false, data: null };
        }

        setTimeout(() => {
          try {
            printWindow.focus();
            printWindow.print();
          } catch (e) {
            console.error(e);
          }
        }, 1500);

        setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
        }, 60000);

        return { hasErrors: false, data: null };
      })
      .catch((error: AxiosError) => {
        return this.handleErrors(error);
      });
  }
}

const getExtensionFromContentDisposition = (contentDisposition?: string) => {
  if (!contentDisposition) return null;

  const match = contentDisposition.match(/filename="?([^";]+)"?/);
  if (!match) return null;

  const extractedFilename = match[1];

  return extractedFilename.split('.').pop();
};

const getExtensionFromContentType = (contentType?: string) => {
  switch (contentType) {
    case 'application/pdf':
      return 'pdf';
    case 'image/jpeg':
    case 'image/jpg':
      return 'jpeg';
    case 'image/png':
      return 'png';
    case 'image/gif':
      return 'gif';
    default:
      return null;
  }
};

export const getAuthHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${getUser()?.access_token ?? ''}`,
  Language: Http.getLanguage(),
});

export const ajax = new Http();
